import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import addNewGridColumnSettings, {
  gridColumnsSettingsType,
} from '~/grid/utils/addNewGridColumnSettings';
import CarouselContext from '~/shared/components/CarouselContext';
import CarouselNavigation from '~/shared/components/CarouselNavigation';
import { useAsset } from '~/techstyle-shared/react-assets';
import {
  desktop,
  useBreakpoint,
  Grid,
  withThemeProps,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import MarketingCard from '../MarketingCard';
import MarketingCardsCarousel from '../MarketingCardsCarousel';

const DynamicFormattedMessage = FormattedMessage;

const MarketingCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const MarketingCardsHeader = styled.div`
  margin: 24px 0;
  ${(props) => props?.marketingCardsHeader}
`;

const MarketingCardsTitle = styled.div`
  margin-bottom: 18px;

  ${desktop`
    margin-bottom: 36px;
  `}

  ${(props) => props?.cardsHeaderTitle};
`;

const MarketingCardsCarouselContainer = styled.div`
  width: 100%;
  position: relative;
`;

const CarouselNavigationContainer = styled.div`
  position: absolute;
  right: 0;
  top: -48px;
`;

const MarketingCardsGrid = styled(Grid)`
  ${(props) => props?.cardsContainer};
`;

function MarketingCards({
  headerTitle,
  container,
  mobileContainer,
  gridColumnsSettings,
  isCarousel,
  mobileCardLayout,
  hideNavigationMobile,
  cardVariant,
  gender,
  refs,
  ...rest
}) {
  const { isMobile } = useBreakpoint();
  const mobileCardLayoutValue =
    mobileCardLayout === 'vertical' && isMobile ? 'column' : 'row';

  const assetContainer = isMobile ? mobileContainer : container;
  const { data } = useAsset(assetContainer, { multi: true });
  const assets = data?.assets ?? [];

  const isCarouselAssets = isMobile ? isCarousel.mobile : isCarousel.desktop;
  const hasMarketingCardsTitle =
    Boolean(headerTitle) && Object.keys(headerTitle).length > 0;
  const {
    text: marketingCardsText,
    textRb: marketingCardsTextRb,
    textStyle: marketingCardsTitleStyle,
  } = hasMarketingCardsTitle && headerTitle;

  const breakpoints = addNewGridColumnSettings(gridColumnsSettings);

  return (
    <MarketingCardsContainer {...rest}>
      {hasMarketingCardsTitle && (
        <MarketingCardsHeader {...rest}>
          <MarketingCardsTitle style={marketingCardsTitleStyle} {...rest}>
            <DynamicFormattedMessage
              id={marketingCardsTextRb}
              defaultMessage={marketingCardsText}
            />
          </MarketingCardsTitle>
        </MarketingCardsHeader>
      )}
      {isCarouselAssets ? (
        <CarouselContext>
          <MarketingCardsCarouselContainer>
            {(!isMobile || !hideNavigationMobile) && (
              <CarouselNavigationContainer>
                <CarouselNavigation isAssetCarouselGrid={isCarouselAssets} />
              </CarouselNavigationContainer>
            )}
            <MarketingCardsCarousel
              assets={assets}
              cardVariant={cardVariant}
              mobileCardLayout={mobileCardLayoutValue}
              refs={refs}
            />
          </MarketingCardsCarouselContainer>
        </CarouselContext>
      ) : (
        <MarketingCardsGrid breakpoints={breakpoints} {...rest}>
          {assets.map((asset, index) => (
            <MarketingCard
              asset={asset}
              index={index}
              key={index}
              cardVariant={cardVariant}
              mobileCardLayout={mobileCardLayoutValue}
              gender={gender}
              refs={refs}
            />
          ))}
        </MarketingCardsGrid>
      )}
    </MarketingCardsContainer>
  );
}

MarketingCards.propTypes = {
  cardVariant: PropTypes.string,
  container: PropTypes.string,
  gender: PropTypes.string,
  gridColumnsSettings: gridColumnsSettingsType,
  headerTitle: PropTypes.object,
  hideNavigationMobile: PropTypes.bool,
  isCarousel: PropTypes.object,
  mobileCardLayout: PropTypes.string,
  mobileContainer: PropTypes.string,
  refs: PropTypes.object,
};

export default withThemeProps(MarketingCards, {
  section: 'marketingCards',
  defaultVariant: 'default',
});
